import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { http, API_BASE } from "@/lib/api";
import WeatherPulseNav from "./WeatherPulseNav";
import { Warning } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

/** Global Weather Pulse map — live community observations, fuzzed coords. */
export default function WeatherMap() {
  const [data, setData] = useState(null);

  useEffect(() => {
    http.get("/weather-pulse/map").then(({ data }) => setData(data)).catch(() => setData({ points: [], count: 0 }));
  }, []);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-map-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="The global weather map — community reports and tracked events worldwide." testidPrefix="ltbar-wp-map" />
        <div className="mt-4"><PageGuide pageKey="weather:map" testidPrefix="wp-map" /></div>
      </div>
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ The living map</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">The global weather map.</h1>
        <p className="mt-2 text-sm text-[#3A3830] max-w-2xl">
          Every dot is a person looking at the sky and recording what they see — last 24 hours. Locations are shown on an approximate grid to protect observers' privacy.
        </p>

        <div className="mt-6 rounded-2xl overflow-hidden border border-black/10 bg-white" style={{ height: 480 }} data-testid="wp-map">
          <MapContainer center={[20, 10]} zoom={2} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {(data?.points || []).map((p) => (
              <CircleMarker
                key={p.id}
                center={[p.lat, p.lon]}
                radius={p.severe ? 9 : 6}
                pathOptions={{
                  color: p.severe ? "#dc2626" : "#B85F2A",
                  fillColor: p.severe ? "#dc2626" : "#B85F2A",
                  fillOpacity: p.severe ? 0.55 : 0.4,
                  weight: 1.5,
                }}
              >
                <Popup>
                  <div data-testid={`wp-map-popup-${p.id}`} style={{ maxWidth: 220 }}>
                    <b>{p.emoji} {p.label}</b>
                    <div style={{ fontSize: 12, color: "#3A3830" }}>
                      {[p.city, p.country].filter(Boolean).join(", ") || `${p.lat}, ${p.lon}`}
                      {p.temperature_c != null && <> · {p.temperature_c}°C</>}
                      {p.rainfall_mm != null && <> · {p.rainfall_mm} mm</>}
                    </div>
                    {p.photo && <img src={abs(p.photo)} alt="" style={{ marginTop: 6, borderRadius: 8, width: "100%" }} />}
                    <div style={{ fontSize: 10, marginTop: 4, color: "#777" }}>Community observation · {(p.created_at || "").slice(5, 16).replace("T", " ")} UTC</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[#3A3830]">
          <span data-testid="wp-map-count"><b>{data?.count ?? "…"}</b> observations in the last 24 h</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#B85F2A" }} /> community report</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#dc2626" }} /> severe condition</span>
        </div>

        <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2" data-testid="wp-map-warning-note">
          <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div>This map shows community observations, not official warnings. For severe weather always follow your national meteorological service.</div>
        </div>
      </div>
    </main>
  );
}
